import React, {useState, useEffect} from 'react';
import {Container, Typography, Button, Alert} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AlarmCard from './AlarmCard';
import AlarmModal from './AlarmModal';

const AlarmDashboard = ({alarms, displayInfo, onUpdate, allTags, setSX, onClose, disabled, selectedHornId}) => {
    const [openModal, setOpenModal] = useState(false);
    const [editingAlarm, setEditingAlarm] = useState(null);
    const [alarmsData, setAlarmsData] = useState(alarms);
    const [revertAvailabilityChanged, setRevertAvailabilityChanged] = useState(false);

    const computeNextAlarmId = (list) => {
        const numericIds = list
            .map((item) => Number.parseInt(item.id, 10))
            .filter((value) => Number.isFinite(value));
        if (numericIds.length === 0) {
            return 1;
        }
        return Math.max(...numericIds) + 1;
    };

    const deepCopyAlarm = (alarm) => {
        if (!alarm) {
            return {};
        }

        if (typeof structuredClone === 'function') {
            return structuredClone(alarm);
        }

        try {
            return JSON.parse(JSON.stringify(alarm));
        } catch (copyError) {
            return {...alarm};
        }
    };

    const buildCloneRule = (rule, existingRules) => {
        const normalizedExisting = new Set(
            existingRules
                .map((value) => (value ?? '').trim().toLowerCase())
        );

        const baseRule = (rule ?? '').trim();
        const cloneBase = baseRule.length ? `${baseRule} (copy)` : 'Alarm (copy)';

        let candidate = cloneBase;
        let suffix = 2;
        while (normalizedExisting.has(candidate.trim().toLowerCase())) {
            candidate = `${cloneBase} ${suffix}`;
            suffix += 1;
        }

        return candidate;
    };

    const handleOpenModal = (alarm = null) => {
        setEditingAlarm(alarm);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setEditingAlarm(null);
        setOpenModal(false);
    };

    const handleSaveAlarm = (newAlarm) => {
        let data = [];
        if (editingAlarm) {
            data = alarmsData.map(alarm => alarm.id === newAlarm.id ? newAlarm : alarm);
        } else {
            // create an alarm with the ID last +1 of the alarmsData
            let id = 1;
            if (alarmsData.length > 0) {
                id = Math.max(...alarmsData.map(alarm => alarm.id)) + 1;
            }
            newAlarm = {
                ...newAlarm,
                id: id,
            };
            data = [newAlarm, ...alarmsData];
        }
        setAlarmsData(data);
        onUpdate(data);
        setOpenModal(false);
    };

    const handleCloneAlarm = (alarmToClone) => {
        const clonedAlarm = deepCopyAlarm(alarmToClone);
        const nextId = computeNextAlarmId(alarmsData);
        const nextRule = buildCloneRule(clonedAlarm.rule, alarmsData.map((item) => item.rule));

        const clonePayload = {
            ...clonedAlarm,
            id: nextId,
            rule: nextRule,
        };

        const nextData = [clonePayload, ...alarmsData];
        setAlarmsData(nextData);
        onUpdate(nextData);
        setEditingAlarm(clonePayload);
    };

    const handleToggleNotifications = (alarmId) => {
        const nextData = alarmsData.map((alarm) => {
            if (alarm.id !== alarmId) {
                return alarm;
            }

            const enabled = alarm.notifications_enabled !== false;
            return {
                ...alarm,
                notifications_enabled: !enabled,
            };
        });

        setAlarmsData(nextData);
        onUpdate(nextData);
    };

    const handleDeleteAlarm = (alarmId) => {
        let data = alarmsData.filter(alarm => alarm.id !== alarmId);
        setAlarmsData(data);
        onUpdate(data);
    };

    useEffect(() => {
        if (openModal) {
            setSX({sx: {width: '50%'}});
        } else {
            setSX({sx: {width: '70%'}});
        }
    }, [openModal]);

    useEffect(() => {
        const handleSimpleAlarmConversionCleared = () => {
            setRevertAvailabilityChanged(true);
        };

        window.addEventListener('item:simpleAlarmConversion:cleared', handleSimpleAlarmConversionCleared);

        return () => {
            window.removeEventListener('item:simpleAlarmConversion:cleared', handleSimpleAlarmConversionCleared);
        };
    }, []);

    if (openModal) {
        return (
            <AlarmModal
                key={'modal'}
                handleClose={handleCloseModal}
                handleSave={handleSaveAlarm}
                handleClone={handleCloneAlarm}
                displayInfo={displayInfo}
                initialAlarm={editingAlarm}
                selectedHornId={selectedHornId}
            />
        );
    }

    return (
        <Container sx={{mt: 4}}>
            <Typography variant="h4" sx={{mb: 3}}>
                🚨 Alarm Management
            </Typography>
            {!disabled && revertAvailabilityChanged && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    This converted alarm set was changed in Alarm Management. Saving the page will keep <strong>Revert to simple alarms</strong> disabled for this set.
                </Alert>
            )}
            {!disabled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    If zone/classes were changed, click <strong>Save Status</strong> on the main page and reopen Alarm Management to refresh conditions.
                </Alert>
            )}
            {!disabled && (<Button variant="contained" startIcon={<AddIcon/>} onClick={() => handleOpenModal()}>
                Add Alarm
            </Button>)}
            <div style={{marginTop: '20px'}}>
                {alarmsData.map((alarm, idx) => (
                    <AlarmCard trafficLights={displayInfo.find((info) => Number(info.zone.value) === Number(alarm.zone_id))?.zone?.traffic_lights || []}
                        key={'card' + idx + alarm.rule}
                        disabled={disabled}
                        alarm={alarm}
                        allTags={allTags}
                        onEdit={() => handleOpenModal(alarm)}
                        onClone={() => handleCloneAlarm(alarm)}
                        onDelete={() => handleDeleteAlarm(alarm.id)}
                        onToggleNotifications={() => handleToggleNotifications(alarm.id)}
                    />
                ))}
            </div>

        </Container>
    );
};

export default AlarmDashboard;
